import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/lib/constants";
import { useConnectModal } from "@/context/ConnectModalContext";
import Logo from "@/components/Logo";

const linkBase = "relative px-3.5 py-2 text-[14px] font-semibold transition-colors whitespace-nowrap";

function NavItem({ l, mobile, i = 0 }) {
  const cls = mobile
    ? "hp-menu-item flex items-center justify-between min-h-[52px] px-1 text-[17px] font-semibold text-hop-blue border-b border-slate-100 active:bg-slate-50"
    : `${linkBase} text-slate-600 hover:text-[var(--hop-brand)]`;
  if (l.external) {
    return (
      <a
        href={l.to}
        target="_blank"
        rel="noopener noreferrer"
        className={cls}
        style={{ "--i": i }}
        aria-label={`${l.label} (opens applykar.in in a new tab)`}
        data-testid={`nav-${l.label.toLowerCase()}`}
      >
        <span className="inline-flex items-center gap-1">
          {l.label} <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
        </span>
      </a>
    );
  }
  return (
    <NavLink
      to={l.to}
      style={{ "--i": i }}
      data-testid={`nav-${l.label.toLowerCase().replace(/\W+/g, "-")}`}
      className={({ isActive }) => `${cls} ${isActive ? "text-[var(--hop-brand)]" : ""}`}
    >
      {({ isActive }) => (
        <>
          {l.label}
          {isActive && mobile && <span className="w-1.5 h-1.5 rounded-full bg-[var(--hop-brand)]" aria-hidden="true" />}
          {isActive && !mobile && <span className="absolute left-3.5 right-3.5 -bottom-px h-0.5 bg-[var(--hop-brand)]" />}
        </>
      )}
    </NavLink>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openConnectModal } = useConnectModal();
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onResize = () => { if (window.innerWidth >= 1024) setOpen(false); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const speak = () => { setOpen(false); openConnectModal(); };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 bg-white/97 backdrop-blur transition-all duration-300 ${
          scrolled
            ? "border-b border-slate-200/80 shadow-[0_1px_0_rgba(0,0,0,0.04),0_8px_24px_-16px_rgba(10,23,63,0.18)]"
            : "border-b border-slate-200/60"
        }`}
      >
        <div className="hp-rule" aria-hidden="true" />
        <div
          className={`hop-container flex items-center justify-between transition-all duration-300 ${
            scrolled ? "h-[60px] md:h-[66px]" : "h-[68px] md:h-[76px]"
          }`}
        >
          <Link
            to="/"
            aria-label="House of Projects home"
            data-testid="nav-logo"
            className="flex items-center gap-3 min-w-0"
          >
            <Logo size={scrolled ? 38 : 46} />
            <span
              className={`font-display font-bold text-hop-blue uppercase tracking-widest flex flex-col justify-center hidden min-[380px]:flex transition-all duration-300 ${
                scrolled ? "text-[9px] leading-[1.15]" : "text-[11px] leading-[1.15]"
              }`}
            >
              <span>House</span>
              <span>of</span>
              <span>Projects</span>
            </span>
          </Link>

          <nav aria-label="Primary" className="hidden lg:flex items-center gap-0.5">
            {NAV_LINKS.map((l) => <NavItem key={l.label} l={l} />)}
          </nav>

          <div className="hidden lg:block">
            <button
              type="button"
              onClick={openConnectModal}
              className="hp-btn hp-btn-primary"
              data-testid="nav-cta"
              style={{ padding: scrolled ? ".75rem 1.25rem" : undefined, fontSize: scrolled ? ".875rem" : undefined }}
            >
              Speak with our team <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>

          <button
            type="button"
            className={`hp-burger lg:hidden ${open ? "is-open" : ""}`}
            onClick={() => setOpen((s) => !s)}
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-mobile-toggle"
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* Mobile menu backdrop */}
      <div
        className={`hp-menu-scrim lg:hidden ${open ? "is-open" : ""}`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />
      <div
        id="mobile-menu"
        className={`hp-menu lg:hidden ${open ? "is-open" : ""}`}
        inert={!open}
      >
        <nav aria-label="Mobile" className="hop-container pt-2 pb-5 flex flex-col">
          {NAV_LINKS.map((l, i) => <NavItem key={l.label} l={l} i={i} mobile />)}
          <button
            type="button"
            onClick={speak}
            className="hp-btn hp-btn-primary hp-menu-item mt-5 w-full justify-center"
            style={{ "--i": NAV_LINKS.length }}
          >
            Speak with our team <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </nav>
      </div>
    </>
  );
}
